import BaseGridScroll from '../GridScroll';
import BaseTileWrapper from '../Tile';
import HealthStatMeter from './HealthStatMeter';
import MetricIcon from './MetricIcon';
import MetricTooltipContent from './MetricTooltipContent';
import PricePointMetricIcon from './PricePointMetricIcon';
import Tooltip from '../Tooltip';
import styled from 'styled-components';
import { tooltips } from '@/copy';

const TILE_HEIGHT = '10.125rem';

const TileWrapper = styled(BaseTileWrapper)`
    min-height: ${TILE_HEIGHT};
    border: 0;
    display: flex;
    flex-direction: column;
    padding: 0px;
    text-transform: unset;
`;

function MetricAndLabel({ metric, metricLabel }) {
    return (
        <div>
            <Metric>{metric}</Metric>
            {tooltips.healthStats[metricLabel].abbrTitleHTML ? (
                <MetricLabel
                    dangerouslySetInnerHTML={{
                        __html: tooltips.healthStats[metricLabel].abbrTitleHTML,
                    }}
                />
            ) : (
                <MetricLabel>{metricLabel}</MetricLabel>
            )}
        </div>
    );
}

function DefaultHealthStatInner({ metric, metricLabel, score, scoreLabel }) {
    return (
        <>
            <HealthStatMeter
                score={score}
                scoreLabel={scoreLabel}
                metricLabel={metricLabel}
            />
            <MetricAndLabel metric={metric} metricLabel={metricLabel} />
        </>
    );
}

function PricePointHealthStatInner({ score, scoreLabel }) {
    return (
        <PricePointHealthStat>
            <PricePointLabel>{scoreLabel}</PricePointLabel>
            <PricePointMetricIcon
                showEmpty={true}
                score={score}
                css={`
                    color: ${({ theme }) => theme.purple_1};
                `}
            />
        </PricePointHealthStat>
    );
}

function PopularityShareHealthStatInner({ metric, metricLabel, scoreLabel }) {
    return (
        <>
            <PopularityShareLabel>{scoreLabel}</PopularityShareLabel>
            <MetricAndLabel metric={metric} metricLabel={metricLabel} />
        </>
    );
}

function HealthStatInner(props) {
    if (props.metricLabel === 'price point')
        return <PricePointHealthStatInner {...props} />;
    if (props.metricLabel === 'popularity share')
        return <PopularityShareHealthStatInner {...props} />;

    return <DefaultHealthStatInner {...props} />;
}

function HealthStatTile({ metric, metricLabel, score, scoreLabel, ...props }) {
    return (
        <TileWrapper isInteractive={false} {...props}>
            <HealthStatIcon>
                <MetricIcon metricLabel={metricLabel} />
            </HealthStatIcon>
            <MetricSubtitle>
                {tooltips.healthStats[metricLabel].subtitle}
                <Tooltip
                    content={<MetricTooltipContent metricLabel={metricLabel} />}
                    width="500px"
                    mixpanelMeta={{ contentKey: metricLabel }}
                    openOnClick={true}
                />
            </MetricSubtitle>
            <HealthStatInner
                metric={metric}
                metricLabel={metricLabel}
                score={score}
                scoreLabel={scoreLabel}
            />
        </TileWrapper>
    );
}

const TILE_WIDTH = 11.5;

const GridScrollPadding = 20;

const GridScroll = styled(BaseGridScroll).attrs(
    ({ padding = GridScrollPadding, gap = 24, ...props }) => ({
        padding,
        gap,
        ...props,
    }),
)`
    & > li {
        min-width: ${TILE_WIDTH}rem;
        flex: 0 0 calc(25% - ${({ gap }) => `${gap}px + ${gap}px`} / 4);
    }

    @media screen and (min-width: ${({ theme }) => theme.bp.sm}px) {
        & > li {
            min-width: ${TILE_WIDTH}rem;
        }
    }
`;

const Message = styled.div`
    color: ${({ theme }) => theme.gray_2};
    font-weight: 500;
    min-height: ${TILE_HEIGHT};
    display: flex;
    justify-content: center;
    align-items: center;
`;

function HealthStatTiles({
    tiles,
    status,
    Grid = GridScroll,
    Tile = HealthStatTile,
    ...props
}) {
    if (['loading', 'idle'].includes(status))
        return (
            <Grid>
                <TileWrapper isLoading={true} />
            </Grid>
        );
    if (status === 'error')
        return <Message>Unable to load health stats</Message>;
    if (tiles.length < 1)
        return <Message>Not enough data to show health stats</Message>;

    return (
        <Grid {...props}>
            {tiles.map((tile) => (
                <Tile key={tile.metricLabel} {...tile} />
            ))}
        </Grid>
    );
}

function HealthStats({ stats, fetching, ...props }) {
    return (
        <HealthStatTiles
            $statsLength={stats?.length}
            tiles={stats}
            status={!stats && fetching ? 'loading' : stats ? 'success' : 'idle'}
            {...props}
        />
    );
}

const PricePointHealthStat = styled.div`
    background-color: ${({ theme }) => theme.gray_6};
    border: 1px solid ${({ theme }) => theme.gray_5};
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    padding: ${({ theme }) => theme.space_8}px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 4em;
`;

const PricePointLabel = styled.span`
    font-size: 0.875em;
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ theme }) => theme.gray_3};
`;

const PopularityShareLabel = styled.span`
    font-size: 1em;
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ theme }) => theme.black};
`;

export const Metric = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.gray_1};
    text-transform: uppercase;
    &::after {
        content: '\\00A0';
    }
`;

export const MetricLabel = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.gray_3};
    text-transform: capitalize;
`;

const MetricSubtitle = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.gray_1};
    text-wrap: balance;
    flex-grow: 1;
`;

const HealthStatIcon = styled.div`
    font-size: 20px;
    color: ${({ theme }) => theme.gray_4};
`;

export default HealthStats;
